import React, { useState, useEffect } from 'react';
import { FileText, Download, Upload, Check, AlertCircle, Calendar, DollarSign, Plus, X, Eye } from 'lucide-react';
import taxFormService from '../../../services/taxFormsService';
import W9Form from '../../TaxForms/W9Form';
import W8BENForm from '../../TaxForms/W8BENForm';
import FormPreview from '../../TaxForms/FormPreview';
import api from '../../../services/api'; // Import the api instance
import './TaxForms.css';

const TaxForms = ({ agentId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [submittedForms, setSubmittedForms] = useState([]); // W-9/W-8BEN forms
  const [generated1099s, setGenerated1099s] = useState([]); // Generated 1099s
  const [selectedForm, setSelectedForm] = useState(null);
  const [uploadMode, setUploadMode] = useState(false);
  const [fillMode, setFillMode] = useState(null); // 'w9' or 'w8ben'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);
  const [userCountry, setUserCountry] = useState('US');
  const [previewForm, setPreviewForm] = useState(null);

  useEffect(() => {
    if (agentId) {
      fetchTaxForms();
      fetchUserProfile();
    } else {
      setError('Agent ID not found');
      setLoading(false);
    }
  }, [agentId]);

  const fetchUserProfile = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.country) {
        setUserCountry(user.country);
      }
    } catch (err) {
      console.error('Error getting user profile:', err);
    }
  };

  const fetchTaxForms = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching tax forms for agentId:', agentId);
      
      // Fetch agent-specific tax forms data (includes both submitted forms and generated 1099s)
      const agentResponse = await taxFormService.getAgentTaxForms(agentId);
      
      console.log('✅ Agent tax forms response:', agentResponse);
      
      // The backend returns structured data with submittedForms and generated1099s
      if (agentResponse && typeof agentResponse === 'object') {
        const submittedForms = agentResponse.submittedForms || [];
        const generated1099s = agentResponse.generated1099s || [];
        
        console.log('📋 Submitted forms:', submittedForms);
        console.log('📋 Generated 1099s:', generated1099s);
        
        setSubmittedForms(submittedForms);
        setGenerated1099s(generated1099s);
      } else {
        // Fallback: if response is an array, treat as submitted forms only
        setSubmittedForms(Array.isArray(agentResponse) ? agentResponse : []);
        setGenerated1099s([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching tax forms:', err);
      setError('Failed to load tax forms');
      setSubmittedForms([]);
      setGenerated1099s([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData, formType) => {
    try {
      setLoading(true);
      
      // Submit the tax form
      if (formType === 'w9') {
        await taxFormService.submitW9({ ...formData, agent_id: agentId });
      } else {
        await taxFormService.submitW8BEN({ ...formData, agent_id: agentId });
      }
      
      await fetchTaxForms();
      setFillMode(null);
      setError(null);
    } catch (err) {
      setError(`Failed to submit ${formType.toUpperCase()} form`);
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormUpload = async (formType, file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('form', file);
      formData.append('formType', formType);
      
      await taxFormService.uploadTaxForm(agentId, formData);
      await fetchTaxForms();
      setUploadMode(false);
      setError(null);
    } catch (err) {
      setError('Failed to upload form');
      console.error('Error uploading form:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadForm = async (formId, formType) => {
    try {
      console.log(`🔽 Attempting to download ${formType} form with ID:`, formId);
      
      // Show a user-friendly message that PDF download is not yet implemented
      alert('PDF download is not yet implemented.\n\nPlease use the Preview feature to view form data.');
      
      // Future implementation would call the download endpoint:
      // await taxFormService.downloadTaxForm(formId);
      
    } catch (err) {
      console.error('Error downloading form:', err);
      if (err.response && err.response.status === 501) {
        alert('PDF generation is not yet implemented.\n\nPlease use the Preview feature to view form data.');
      } else {
        alert('Failed to download form. Please try again later.');
      }
    }
  };

  const getFormStatus = (form) => {
    if (!form) return 'missing';
    if (form.status === 'pending') return 'pending';
    return 'completed';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Update selected year when tax forms are loaded
  useEffect(() => {
    if (generated1099s && generated1099s.length > 0) {
      // Find the most recent 1099 form year
      const mostRecentYear = Math.max(...generated1099s.map(form => form.tax_year));
      setSelectedYear(mostRecentYear);
      console.log('🗓️ Setting selected year to most recent 1099:', mostRecentYear);
    }
  }, [generated1099s]);

  if (loading) {
    return (
      <div className="tax-forms-loading">
        <div className="spinner"></div>
        <p>Loading tax forms...</p>
      </div>
    );
  }

  // If in fill mode, show the form component
  if (fillMode) {
    return (
      <div className="tax-forms-modal-overlay" onClick={() => setFillMode(null)}>
        <div className="tax-forms-modal tax-form-fill-mode" onClick={(e) => e.stopPropagation()}>
          <div className="tax-forms-header">
            <h2>Fill Out {fillMode === 'w9' ? 'W-9' : 'W-8BEN'} Form</h2>
            <button className="close-btn" onClick={() => setFillMode(null)}>×</button>
          </div>
          <div className="tax-form-container">
            {fillMode === 'w9' ? (
              <W9Form 
                onSubmit={(data) => handleFormSubmit(data, 'w9')}
                onCancel={() => setFillMode(null)}
              />
            ) : (
              <W8BENForm 
                onSubmit={(data) => handleFormSubmit(data, 'w8ben')}
                onCancel={() => setFillMode(null)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tax-forms-modal-overlay" onClick={onClose}>
      <div className="tax-forms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tax-forms-header">
          <h2>Tax Forms</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div className="tax-forms-content">
          {/* W-9 / W-8BEN Section */}
          <div className="tax-form-section">
            <h3>Tax Identification Forms</h3>
            
            {/* W-9 Forms History */}
            <div className="form-type-section">
              <div className="form-type-header">
                <h4>W-9 Forms (US Residents)</h4>
                <button 
                  className="fill-form-btn"
                  onClick={() => setFillMode('w9')}
                >
                  <Plus size={16} />
                  Fill New W-9
                </button>
              </div>
              
              {submittedForms && submittedForms.filter(form => form.form_type === 'w9').length > 0 ? (
                <div className="submitted-forms-list">
                  {submittedForms.filter(form => form.form_type === 'w9').map((form, index) => (
                    <div key={form.id} className="submitted-form-item">
                      <div className="form-info">
                        <div className="form-icon">
                          <FileText size={20} />
                        </div>
                        <div className="form-details">
                          <h5>Submitted W-9 {formatDate(form.created_at)}</h5>
                          <p className="form-meta">
                            Version {form.version} • Submitted on {formatDate(form.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button 
                          className="preview-btn"
                          onClick={() => setPreviewForm({ type: 'w9', data: form })}
                        >
                          <Eye size={16} />
                          Preview
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-forms-message">
                  <p>No W-9 forms submitted yet.</p>
                  <button 
                    className="fill-form-btn primary"
                    onClick={() => setFillMode('w9')}
                  >
                    <Plus size={16} />
                    Submit Your First W-9
                  </button>
                </div>
              )}
            </div>

            {/* W-8BEN Forms History */}
            <div className="form-type-section">
              <div className="form-type-header">
                <h4>W-8BEN Forms (Non-US Residents)</h4>
                <button 
                  className="fill-form-btn"
                  onClick={() => setFillMode('w8ben')}
                >
                  <Plus size={16} />
                  Fill New W-8BEN
                </button>
              </div>
              
              {submittedForms && submittedForms.filter(form => form.form_type === 'w8ben').length > 0 ? (
                <div className="submitted-forms-list">
                  {submittedForms.filter(form => form.form_type === 'w8ben').map((form, index) => (
                    <div key={form.id} className="submitted-form-item">
                      <div className="form-info">
                        <div className="form-icon">
                          <FileText size={20} />
                        </div>
                        <div className="form-details">
                          <h5>Submitted W-8BEN {formatDate(form.created_at)}</h5>
                          <p className="form-meta">
                            Version {form.version} • Submitted on {formatDate(form.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button 
                          className="preview-btn"
                          onClick={() => setPreviewForm({ type: 'w8ben', data: form })}
                        >
                          <Eye size={16} />
                          Preview
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-forms-message">
                  <p>No W-8BEN forms submitted yet.</p>
                  <button 
                    className="fill-form-btn primary"
                    onClick={() => setFillMode('w8ben')}
                  >
                    <Plus size={16} />
                    Submit Your First W-8BEN
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 1099 Forms Section */}
          <div className="tax-form-section">
            <h3>1099 Forms</h3>
            <div className="year-selector">
              <label>Tax Year:</label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>

            <div className="form1099-list">
              {generated1099s && generated1099s.filter(form => form.tax_year === selectedYear).length > 0 ? (
                generated1099s
                  .filter(form => form.tax_year === selectedYear)
                  .map(form => (
                    <div key={form.id} className="form1099-item">
                      <div className="form1099-info">
                        <FileText size={20} />
                        <div>
                          <h5>1099-MISC {form.tax_year}</h5>
                          <p>Total: ${form.total_amount.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="form1099-actions">
                        <span className="issue-date">
                          <Calendar size={14} />
                          Issued: {formatDate(form.generated_date)}
                        </span>
                        <button 
                          className="download-btn small"
                          onClick={() => handleDownloadForm(form.id, '1099-MISC')}
                        >
                          <Download size={14} />
                          Download
                        </button>
                      </div>
                    </div>
                  ))
              ) : (
                <div className="no-forms-message">
                  <p>No 1099 forms available for {selectedYear}</p>
                  <p className="text-sm text-gray-600">
                    1099 forms are generated by your agency after year-end.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Commission Summary */}
          <div className="commission-summary">
            <h3>Commission Summary</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">YTD Commissions</span>
                <span className="stat-value">
                  <DollarSign size={16} />
                  {generated1099s.reduce((acc, form) => acc + form.total_amount, 0).toLocaleString() || '0'}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Tax Documents</span>
                <span className="stat-value">
                  {(submittedForms.length > 0 ? 1 : 0) + generated1099s.length}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="tax-forms-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Upload Modal */}
        {uploadMode && (
          <div className="upload-modal-overlay" onClick={() => setUploadMode(false)}>
            <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Upload {uploadMode.toUpperCase()} Form</h3>
              <div className="upload-area">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleFormUpload(uploadMode, e.target.files[0]);
                    }
                  }}
                  id="form-upload"
                />
                <label htmlFor="form-upload">
                  <Upload size={32} />
                  <p>Click to select file or drag and drop</p>
                  <span>PDF, JPG, or PNG (max 10MB)</span>
                </label>
              </div>
              <button 
                className="cancel-btn"
                onClick={() => setUploadMode(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewForm && (
          <div className="preview-modal-overlay" onClick={() => setPreviewForm(null)}>
            <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
              <FormPreview 
                formType={previewForm.type} 
                formData={previewForm.data} 
                onClose={() => setPreviewForm(null)} 
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxForms;
